import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ShieldCheck,
  FileText,
  Copy,
  Check,
  ExternalLink,
  ChevronRight,
  ArrowRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/global/SEO';
import EditorialHero from '@/components/ui/EditorialHero';

interface IntellectualPropertyPageProps {
  lang: 'en' | 'ar';
}

export const IntellectualPropertyPage: React.FC<IntellectualPropertyPageProps> = ({ lang }) => {
  const isAr = lang === 'ar';
  const [copiedCitation, setCopiedCitation] = useState(false);

  const handleCopyCitation = () => {
    const citation = `VEXL Ecosystem — Registered Software Copyright (EGIPA Deposit No. 5007). Author: Abdelrahman Mohamed Diaaeldin Samir Abushousha. Egyptian Intellectual Property Authority, Law No. 82/2002 & Law No. 163/2023. Issued: July 27, 2026.`;
    navigator.clipboard.writeText(citation);
    setCopiedCitation(true);
    setTimeout(() => setCopiedCitation(false), 2500);
  };

  const registryData = [
    {
      labelEn: 'Official Deposit Number',
      labelAr: 'رقم الإيداع القانوني',
      valueEn: 'EGIPA Deposit No. 5007',
      valueAr: 'رقم الإيداع (5007) — مصنف حاسب آلي',
      isHighlight: true,
    },
    {
      labelEn: 'Governing Legislation',
      labelAr: 'السند التشريعي والقانوني',
      valueEn: 'Law No. 82 of 2002 (Article 186) & Law No. 163 of 2023',
      valueAr: 'قانون حماية حقوق الملكية الفكرية رقم (82) لسنة 2002 وقانون رقم (163) لسنة 2023',
    },
    {
      labelEn: 'Regulatory Authority',
      labelAr: 'الجهة المعتمدة والمصدرة',
      valueEn: 'Egyptian Intellectual Property Authority (EGIPA)',
      valueAr: 'الجهاز المصري للملكية الفكرية (EGIPA)',
    },
    {
      labelEn: 'Registered Software Title',
      labelAr: 'اسم المصنف المسجل',
      valueEn: 'VEXL ECOSYSTEM (منظومة فكسل للذكاء الاصطناعي الجيومكاني)',
      valueAr: 'منظومة فكسل للذكاء الاصطناعي الجيومكاني (VEXL ECOSYSTEM)',
    },
    {
      labelEn: 'Classification & Medium',
      labelAr: 'طبيعة وتصنيف المصنف',
      valueEn: 'Computer Software & Web-Based Spatial System',
      valueAr: 'برنامج حاسب آلي — موقع على الإنترنت',
    },
    {
      labelEn: 'Author & Rights Holder',
      labelAr: 'المؤلف وصاحب الحق',
      valueEn: 'Abdelrahman Mohamed Diaaeldin Samir Abushousha',
      valueAr: 'عبدالرحمن محمد ضياء الدين سمير شوشة',
    },
    {
      labelEn: 'Technical Execution Stack',
      labelAr: 'لغات البرمجة المعتمدة',
      valueEn: 'Python · TypeScript · JavaScript · Node.js',
      valueAr: 'Python – TypeScript – JavaScript – Node.JS',
      isMono: true,
    },
    {
      labelEn: 'Official Filing & Issue Dates',
      labelAr: 'تواريخ الإيداع والإصدار',
      valueEn: 'Filing: June 23, 2026 · Issuance: July 27, 2026',
      valueAr: 'تاريخ الإيداع: 2026/06/23 · تاريخ الإصدار: 2026/07/27',
      isMono: true,
    }
  ];

  return (
    <div
      className={`min-h-screen bg-[#05070A] text-[#EDF0F1] selection:bg-[#0E93A3]/30 ${
        isAr ? 'font-arabic' : 'font-sans'
      } antialiased`}
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <SEO
        title={
          isAr
            ? 'الملكية الفكرية والأصول البرمجية | منظومة فكسل (EGIPA #5007)'
            : 'Intellectual Property & Sovereign Assets | VEXL Ecosystem (EGIPA #5007)'
        }
        description={
          isAr
            ? 'الملف القانوني المعتمد للملكية الفكرية لمنظومة VEXL المسجلة رسمياً لدى الجهاز المصري للملكية الفكرية برقم 5007.'
            : 'Certified software copyright documentation and sovereign intellectual property registry for VEXL Ecosystem (EGIPA Deposit #5007).'
        }
        lang={lang}
      />

      {/* ── 1. Editorial Header ────────────────────────────────────────────── */}
      <section className="pt-20 md:pt-24">
        <div className="w-full max-w-[1400px] mx-auto px-6">
          <EditorialHero
            title={isAr ? 'الملكية الفكرية والأصول البرمجية السيادية' : 'Intellectual Property & Sovereign Assets'}
            description={
              isAr
                ? 'منظومة VEXL هي أصل برمجي جيومكاني سيادي مسجل رسمياً ومحمي قانونياً بموجب قانون حماية حقوق الملكية الفكرية رقم (82) لسنة 2002 وقانون إنشاء الجهاز المصري للملكية الفكرية رقم (163) لسنة 2023 برقم إيداع (5007).'
                : 'VEXL Ecosystem is a sovereign, legally protected GeoAI software asset officially registered under the Egyptian Intellectual Property Authority (EGIPA) pursuant to Law No. 82 of 2002 and Law No. 163 of 2023 (Deposit No. 5007).'
            }
            actions={[
              {
                label: copiedCitation ? (isAr ? 'تم نسخ التوثيق القانوني!' : 'Citation Copied!') : (isAr ? 'نسخ التوثيق القانوني' : 'Copy Legal Citation'),
                onClick: handleCopyCitation,
                variant: 'primary',
                icon: copiedCitation ? <Check size={13} className="text-white" /> : <Copy size={13} />
              },
              {
                label: isAr ? 'شروط الاستخدام والترخيص' : 'Terms of Use & Licensing',
                href: '/terms',
                variant: 'secondary'
              }
            ]}
            isAr={isAr}
          />
        </div>
      </section>

      {/* ── 2. Formal Registration Specification Table ──────────────────────── */}
      <section className="py-16 border-b border-[#262C31]">
        <div className="w-full max-w-[1400px] mx-auto px-6">
          <div className="mb-8 space-y-1">
            <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold">
              {isAr ? 'الملف التوثيقي الرسمي' : 'OFFICIAL REGISTRATION DOSSIER'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#EDF0F1] tracking-tight">
              {isAr ? 'البيانات المعتمدة في السجل القومي للمصنفات' : 'Certified Registry Specifications'}
            </h2>
          </div>

          {/* Clean Monolithic Table */}
          <div className="rounded-md border border-[#262C31] bg-[#12161A] overflow-hidden shadow-sm">
            <table className="w-full text-left border-collapse text-xs sm:text-sm" dir={isAr ? 'rtl' : 'ltr'}>
              <thead>
                <tr className="border-b border-[#262C31] bg-[#171C21] text-[11px] font-mono text-[#9AA5AC] uppercase tracking-wider">
                  <th className="py-4 px-6 font-semibold w-1/3 sm:w-1/4">{isAr ? 'البيان التوثيقي' : 'REGISTRY FIELD'}</th>
                  <th className="py-4 px-6 font-semibold">{isAr ? 'البيانات المسجلة قانونياً' : 'OFFICIAL SPECIFICATION'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#262C31]">
                {registryData.map((row, idx) => (
                  <tr key={idx} className="hover:bg-[#171C21]/50 transition-colors">
                    <td className="py-4 px-6 text-xs font-mono text-[#9AA5AC] font-semibold align-top whitespace-nowrap">
                      {isAr ? row.labelAr : row.labelEn}
                    </td>
                    <td className="py-4 px-6 font-medium text-[#EDF0F1] align-top">
                      {row.isHighlight ? (
                        <span className="font-mono font-bold text-[#0E93A3] text-sm">
                          {isAr ? row.valueAr : row.valueEn}
                        </span>
                      ) : row.isMono ? (
                        <span className="font-mono text-xs text-[#9AA5AC]">
                          {isAr ? row.valueAr : row.valueEn}
                        </span>
                      ) : (
                        <span>{isAr ? row.valueAr : row.valueEn}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ── 3. Protected Architectural Scope (Dual-Engine Framework) ─────────── */}
      <section className="py-16 border-b border-[#262C31] bg-[#05070A]">
        <div className="w-full max-w-[1400px] mx-auto px-6">
          <div className="mb-10 space-y-1">
            <div className="text-xs font-mono uppercase tracking-widest text-[#0E93A3] font-semibold">
              {isAr ? 'نطاق الحماية المعمارية' : 'ARCHITECTURAL SCOPE & COVERAGE'}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#EDF0F1] tracking-tight">
              {isAr ? 'الحزمتان التشغيليتان المعتمدتان في المصنف البرمجي' : 'The Two Certified Operational Engines'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Engine 01 */}
            <div className="p-8 rounded-md border border-[#262C31] bg-[#12161A] space-y-5">
              <div className="flex items-center justify-between border-b border-[#262C31] pb-4">
                <div className="text-xs font-mono text-[#0E93A3] font-bold tracking-widest uppercase">
                  {isAr ? 'الحزمة التشغيلية الأولى // 01' : 'OPERATIONAL ENGINE // 01'}
                </div>
                <span className="text-xs font-mono text-[#9AA5AC]">REG. #5007</span>
              </div>

              <h3 className="text-2xl font-bold text-[#EDF0F1]">
                VEXL NOVA Framework
              </h3>

              <p className="text-xs sm:text-sm text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'منظومة برمجية جيومكانية سيادية متكاملة هندسياً لدمج المتجهات والخلايا الرقمية، تعمل كمساعد ذكي لأتمتة أكواد التحليل الجغرافي والتحقق الطوبولوجي ومساعدة الباحثين والمهندسين.'
                  : 'An integrated sovereign geospatial software pipeline engineering the mathematical fusion of multi-dimensional vector geometries and raster arrays for automated spatial analytics code generation and topological constraint verification.'}
              </p>

              <div className="space-y-2 pt-3 border-t border-[#262C31] text-xs text-[#9AA5AC]">
                <div className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3] mt-1.5 shrink-0" />
                  <span>{isAr ? 'أتمتة سلاسل معالجة البيانات الجغرافية المعقدة وتوليد الأكواد المكانية.' : 'Automated GeoAI code generation and spatial reasoning orchestration.'}</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3] mt-1.5 shrink-0" />
                  <span>{isAr ? 'دمج بيانات المتجهات والراستر في بيئة استدلال موحدة ذات أداء قطعي.' : 'Deterministic vector-raster tensor fusion engine.'}</span>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/products/nova"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0E93A3] hover:underline"
                >
                  <span>{isAr ? 'استكشف منصة VEXL NOVA' : 'Explore VEXL NOVA Framework'}</span>
                  <ChevronRight size={13} className={isAr ? 'rotate-180' : ''} />
                </Link>
              </div>
            </div>

            {/* Engine 02 */}
            <div className="p-8 rounded-md border border-[#262C31] bg-[#12161A] space-y-5">
              <div className="flex items-center justify-between border-b border-[#262C31] pb-4">
                <div className="text-xs font-mono text-[#D48207] font-bold tracking-widest uppercase">
                  {isAr ? 'الحزمة التشغيلية الثانية // 02' : 'OPERATIONAL ENGINE // 02'}
                </div>
                <span className="text-xs font-mono text-[#9AA5AC]">REG. #5007</span>
              </div>

              <h3 className="text-2xl font-bold text-[#EDF0F1]">
                Drought Watch Pro Framework
              </h3>

              <p className="text-xs sm:text-sm text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'منصة تنبؤ استباقي بيئي تعتمد على شبكات التعلم العميق (Bi-LSTM) لمعالجة السلاسل الزمنية للأقمار الصناعية (MODIS, CHIRPS, GLDAS) والتنبؤ بالجفاف الزراعي قبل حدوثه بـ 6 أشهر بدقة 91.3%.'
                  : 'A proprietary deep learning time-series framework (Bi-LSTM) processing multi-satellite data streams to deliver proactive agricultural drought early warning with a 6-month predictive horizon and 91.3% verified model accuracy.'}
              </p>

              <div className="space-y-2 pt-3 border-t border-[#262C31] text-xs text-[#9AA5AC]">
                <div className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D48207] mt-1.5 shrink-0" />
                  <span>{isAr ? 'تنبؤ مكاني متعدد المتغيرات على مستوى البكسل بدقة 250 متراً.' : 'Pixel-level multivariate forecasting at 250m spatial resolution.'}</span>
                </div>
                <div className="flex items-start gap-2.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D48207] mt-1.5 shrink-0" />
                  <span>{isAr ? 'الحصول على الجائزة الثالثة عالمياً في تحدي GESC 2026 (معهد UNEP-Tongji).' : 'Awarded Global 3rd Prize at the 2026 GESC Challenge (UNEP-Tongji).'}</span>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/products/droughtwatch"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#D48207] hover:underline"
                >
                  <span>{isAr ? 'استكشف منصة DroughtWatch' : 'Explore DroughtWatch Framework'}</span>
                  <ChevronRight size={13} className={isAr ? 'rotate-180' : ''} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── 4. Sovereign Integrity & Enterprise Compliance ───────────────────── */}
      <section className="py-16 border-b border-[#262C31]">
        <div className="w-full max-w-[1400px] mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="space-y-2">
              <div className="text-xs font-mono text-[#0E93A3] font-bold uppercase tracking-wider">
                01 // SOVEREIGN_IP_OWNERSHIP
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">
                {isAr ? 'ملكية برمجية أصيلة' : '100% Proprietary IP'}
              </h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'كود المصدر ونماذج الذكاء الاصطناعي مسجلة كأصول رقمية مستقلة لا تعتمد على بنى خارجية قد تهدد سيادة البيانات.'
                  : 'Source code, tensor pipelines, and neural weights are registered sovereign assets guaranteeing operational independence.'}
              </p>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-mono text-[#0E93A3] font-bold uppercase tracking-wider">
                02 // AIR_GAPPED_COMPLIANCE
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">
                {isAr ? 'نشر سحابي ومحلي معزول' : 'Air-Gapped & On-Premises'}
              </h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'بنية قابلة للنشر داخل مراكز البيانات الوطنية الخاصة والمغلقة بدون اتصال خارجي لضمان الأمان والسرية التامة.'
                  : 'Engineered for sovereign data residency, air-gapped on-prem deployments, and national infrastructure compliance.'}
              </p>
            </div>

            <div className="space-y-2">
              <div className="text-xs font-mono text-[#0E93A3] font-bold uppercase tracking-wider">
                03 // LEGAL_CERTAINTY
              </div>
              <h4 className="text-base font-bold text-[#EDF0F1]">
                {isAr ? 'الامتثال القانوني والمؤسسي' : 'Legal & Contractual Integrity'}
              </h4>
              <p className="text-xs text-[#9AA5AC] leading-relaxed">
                {isAr
                  ? 'توثيق رسمي لدى الهيئة القومية يمنح الشركاء والجهات الحكومية أعلى درجات اليقين التعاقدي والشفافية.'
                  : 'Certified statutory deposit grants enterprise and institutional partners complete legal and audit clarity.'}
              </p>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default IntellectualPropertyPage;
